/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: main.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Main entry point for the Ring2All Platform web application.
    Initializes React, routing, internationalization, and theme providers.

 🔧 Notes:
    - Initializes React 18 with createRoot for optimal performance
    - Configures React Router for client-side navigation
    - Sets up i18next for internationalization support
    - Integrates custom ThemeProvider for dynamic theming
    - Configures Sonner toast notifications with theme integration
    - Implements React StrictMode for development optimizations

 🔁 Last Updated: 2026-04-12 by Rodrigo Cuadra — menu hardcoded migration
────────────────────────────────────────────────────────────────*/

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

// Polyfill for crypto.randomUUID in insecure HTTP contexts
if (typeof window !== 'undefined' && window.crypto && !window.crypto.randomUUID) {
  // @ts-ignore
  window.crypto.randomUUID = function () {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };
}

import { I18nextProvider } from 'react-i18next';
import { Toaster } from 'sonner';
import App from './App';
import i18n from './i18n';
import { ThemeProvider } from './components/ThemeProvider';
import './index.css';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider>
          <App />
          <Toaster
            position="top-center"
            theme="system"
            richColors
            closeButton
            duration={3000}
          />
        </ThemeProvider>
      </I18nextProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
